/**
 * The words of a search term, the way Campaigns › Search terms › Wording counts them and the rule
 * engine finds them: lower case, no punctuation, no filler words, a plural the same as its singular.
 */

/** Words that say nothing about what someone wants to buy. */
const FILLER = new Set(
  "a an and are as at be best buy by for from how i in is it my near of on or the to vs what where which with without you your me".split(
    " ",
  ),
);

/** One form for a word and its plural, so "syrups" and "syrup" are the same word. */
export const stem = (w: string) =>
  w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w;

/** The words of a text: lower case, no punctuation, no filler, no single letters. */
export function wordsOf(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 1 && !FILLER.has(w));
}

/**
 * Two words that follow each other in a text, as typed (a phrase negative needs them in that order).
 * A pair with a filler word in it says too little ("syrup for"), so it is left out.
 */
export function pairsOf(text: string): string[] {
  const all = text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i + 1 < all.length; i++) {
    const a = all[i]!;
    const b = all[i + 1]!;
    if (a.length > 1 && b.length > 1 && !FILLER.has(a) && !FILLER.has(b)) out.push(`${a} ${b}`);
  }
  return out;
}

/** A word or two-word phrase in the one form its plurals share: how the Wording list tells words apart. */
export const stemAll = (piece: string) => piece.split(" ").map(stem).join(" ");

/** Every word and two-word phrase of a text, each in its shared form, once. */
export const wordKeysOf = (text: string): Set<string> =>
  new Set([...wordsOf(text).map(stem), ...pairsOf(text).map(stemAll)]);
